import { useCallback, useState } from 'react';
import {
  createCategory,
  createSubcategory,
  createSubmenu,
  deleteCategory,
  deleteSubmenu,
  getAllCategories,
  replaceSubmenus,
  updateCategory,
  updateSubmenu
} from '../firebase/categories';

export function useCategoryManagement(initialCategories = []) {
  const [categories, setCategories] = useState(initialCategories);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await getAllCategories();
    if (result.error) {
      setError(result.error);
    } else {
      setCategories(result.categories);
    }

    setLoading(false);
    return result;
  }, []);

  const createCategoryEntry = useCallback(async (categoryData) => {
    setLoading(true);
    setError(null);

    const result = await createCategory(categoryData);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  const createSubcategoryEntry = useCallback(async (subcategoryData) => {
    setLoading(true);
    setError(null);

    const result = await createSubcategory(subcategoryData);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  const createSubmenuEntry = useCallback(async (submenuData) => {
    setLoading(true);
    setError(null);

    const result = await createSubmenu(submenuData);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  const replaceSubmenuEntries = useCallback(async (subcategory, submenus) => {
    setLoading(true);
    setError(null);

    const result = await replaceSubmenus(subcategory, submenus);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  const updateCategoryEntry = useCallback(async (categoryId, updates) => {
    setLoading(true);
    setError(null);

    const result = await updateCategory(categoryId, updates);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  const deleteCategoryEntry = useCallback(async (categoryId) => {
    setLoading(true);
    setError(null);

    const result = await deleteCategory(categoryId);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  const updateSubmenuEntry = useCallback(async (submenuId, updates) => {
    setLoading(true);
    setError(null);

    const result = await updateSubmenu(submenuId, updates);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  const deleteSubmenuEntry = useCallback(async (submenuId) => {
    setLoading(true);
    setError(null);

    const result = await deleteSubmenu(submenuId);
    if (result.error) {
      setError(result.error);
    } else {
      await fetchCategories();
    }

    setLoading(false);
    return result;
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategoryEntry,
    createSubcategoryEntry,
    createSubmenuEntry,
    replaceSubmenuEntries,
    updateCategoryEntry,
    deleteCategoryEntry,
    updateSubmenuEntry,
    deleteSubmenuEntry
  };
}
